// src/utils/api.tsx

// По умолчанию ставим твой основной API
export let API_BASE = "https://api.24mira.ru";

// Тип дизайна клиента (white-label цвета кнопок)
export type AppDesign = {
  ctaButtonColor: string | null;
  ctaButtonTextColor: string | null;
  primaryButtonColor: string | null;
  primaryButtonTextColor: string | null;
  appBackgroundColor: string | null;
};

// Сюда мы запишем настройки, когда скачаем их
export let APP_CONFIG = {
  brandName: "Mira",
  templateType: "energy_reset",
  saasClientId: null as string | null,
  design: null as AppDesign | null,
};

// Функция, которую мы вызовем в App.tsx, чтобы обновить настройки
export function setDynamicConfig(config: any) {
  if (config.apiBase) API_BASE = config.apiBase;
  if (config.brandName) APP_CONFIG.brandName = config.brandName;
  if (config.templateType) APP_CONFIG.templateType = config.templateType;
  APP_CONFIG.saasClientId = config.saasClientId ?? null;
  APP_CONFIG.design = config.design ?? null;
}

/**
 * Применяет дизайн клиента к document.documentElement через CSS-переменные.
 * Поля с null/undefined НЕ устанавливаются — берутся дефолты из :root в buttons.css.
 * Вызывать после setDynamicConfig() при загрузке приложения.
 */
export function applyDesign(design: AppDesign | null | undefined) {
  if (!design) return;
  const root = document.documentElement;
  const map: Array<[keyof AppDesign, string]> = [
    ["ctaButtonColor", "--color-cta-button"],
    ["ctaButtonTextColor", "--color-cta-button-text"],
    ["primaryButtonColor", "--color-primary-button"],
    ["primaryButtonTextColor", "--color-primary-button-text"],
    ["appBackgroundColor", "--color-app-background"],
  ];
  for (const [key, cssVar] of map) {
    const value = design[key];
    if (value) root.style.setProperty(cssVar, value);
  }
}

export function buildHeaders(json = true): Record<string, string> {
  const h: Record<string, string> = {};
  if (json) h["Content-Type"] = "application/json";
  return h;
}

// Мутации требуют CSRF-заголовка. Задание 3E (02.08.2026): токен читается из
// localStorage (ключ mira_csrf), НЕ из cookie — вторая cookie __Host-ms_csrf_*
// была host-only на api.24mira.ru и принципиально невидима из document.cookie
// на странице 24mira.ru (разные механизмы: отправка cookie — по домену
// запроса, видимость в document.cookie — по домену документа; подтверждено
// живым 403 на PATCH /api/profile/timezone, см. отчёт 3E). Сервер параллельно
// получил независимый рубеж — проверку Origin на мутациях (api/index.js).
const MUTATING_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);
const CSRF_STORAGE_KEY = "mira_csrf";

function readCsrfToken(): string | null {
  try {
    return localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

let _authFailureCallback: (() => void) | null = null;

/** Регистрирует колбэк который вызывается когда сервер подтвердил разлогин (401) */
export function setAuthFailureCallback(cb: () => void) {
  _authFailureCallback = cb;
}

/**
 * Обёртка над fetch с cookie-сессией (Задание 3B).
 * Использовать вместо голого fetch() для защищённых запросов.
 *
 * Правило разграничения (обязательное): 401 от сервера — это «разлогинен»,
 * сетевая ошибка (офлайн) — это «остаёмся внутри». fetch() при обрыве связи
 * бросает исключение раньше проверки res.status; оно НЕ перехватывается
 * здесь и пробрасывается вызывающему как есть, _authFailureCallback при этом
 * не вызывается. Refresh по Bearer-токену (auth.tryRefresh) здесь больше не
 * нужен — сессия скользящая, продлевается сервером через last_seen_at.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const method = (init?.method ?? "GET").toString().toUpperCase();
  const csrfHeaders: Record<string, string> = {};
  if (MUTATING_METHODS.has(method)) {
    const csrfToken = readCsrfToken();
    if (csrfToken) csrfHeaders["X-CSRF-Token"] = csrfToken;
  }

  const res = await fetch(input, {
    ...init,
    credentials: "include",
    headers: {
      ...buildHeaders(false),
      ...csrfHeaders,
      ...(init?.headers as Record<string, string> | undefined),
    },
  });

  if (res.status !== 401) return res;

  // Сервер подтвердил недействительность сессии — гасим локальный
  // оптимистичный флаг входа и уводим на экран входа.
  const { auth } = await import("../api/auth");
  auth.clearSession();
  _authFailureCallback?.();
  return res;
}
